import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  ArrowLeft, Check, FileText, CreditCard, ScanSearch, ScanLine,
  Activity, Layers, AlertTriangle, RefreshCw, Eye,
} from "lucide-react";
import { toast } from "sonner";
import { describeError } from "@/lib/api";
import EraViewModal from "./EraViewModal";
import {
  uploadEob, getEobFile, getEobAnalysis, getEra,
  isExtractionFailed, extractionFailureReason,
  runEraValidation, linkPayee, linkPayer, linkPayeeNpi, acknowledgeReconciliation,
  markEraDownloaded, ENGINE_LABEL,
} from "@/lib/eobEraV2";
import PipelineTile from "./PipelineTile";
import StatusFooter from "./StatusFooter";
import useAutoScroll from "@/lib/useAutoScroll";
import { TID } from "@/lib/testIds";

// Grouped pipeline — structure & copy from the v15 "EOB → ERA conversion" design.
const EOB_PHASES = [
  {
    t: "Remittance & context", icon: CreditCard,
    subs: [
      { t: "Reading payer, check number & paid amount", s: "Reading the EOB header — payer, check number and paid amount…" },
      { t: "Identifying the patient & date of service", s: "Cross-checking the documented patient against your roster…" },
    ],
  },
  {
    t: "Claim matching", icon: ScanSearch,
    subs: [
      { t: "Matching remittance lines to open claims", s: "Matching each service line to your open claims…" },
      { t: "Scoring match confidence", s: "Scoring match confidence for every matched line…" },
    ],
  },
  {
    t: "Adjudication & CARC", icon: Activity,
    subs: [
      { t: "Reading allowed / paid / adjustment per line", s: "Reading the adjudication grid — billed, allowed, paid and adjustments line by line…" },
      { t: "Interpreting CARC / RARC reason codes", s: "Interpreting CARC / RARC reason codes on each line…" },
    ],
  },
  {
    t: "Posting & routing", icon: FileText,
    subs: [
      { t: "Composing the ERA (835) & posting", s: "Assembling the ERA (835) and posting against the matched claim…" },
      { t: "Finalizing", s: "Waiting for revQ to finish extracting & auditing this remit…" },
    ],
  },
];
const FLAT = [];
EOB_PHASES.forEach((p, pi) => p.subs.forEach((s, si) => FLAT.push({ pi, si, ...s })));
const TOTAL_SUBS = FLAT.length;
const phaseStart = (pi) => EOB_PHASES.slice(0, pi).reduce((n, p) => n + p.subs.length, 0);

const money = (v) =>
  v == null ? null : `$${Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Friendly labels for the live upstream extraction stages (progress.stage).
const STAGE_LABELS = {
  queued: "Queued",
  ocr: "Reading the document (OCR)",
  classifying: "Classifying document",
  exploding: "Splitting pages",
  extracting: "Extracting claims & lines",
  finalizing: "Finalizing",
  done: "Extraction done",
};
function deriveProc(e) {
  if (!e) return null;
  return {
    status: e.status,
    stage: e.progress?.stage,
    message: e.progress?.message,
    auditStatus: e.audit_status,
    claimCount: (e.summary || e.extraction_summary)?.claim_count,
    mayBeStalled: e.may_be_stalled,
  };
}
// Turn the live file row into a human-readable { label, sub } for the waiting stage.
function liveStatus(proc) {
  if (!proc) return null;
  if (proc.auditStatus === "complete")
    return { label: `Completed · Audit ${proc.claimCount != null ? proc.claimCount + " " : ""}✓`, sub: "All checks passed — assembling your ERA." };
  if (proc.auditStatus === "auditing" || (proc.status === "processed" && proc.auditStatus))
    return { label: "Under audit", sub: "Auditing the extracted claims for accuracy…" };
  if (proc.status === "processed")
    return { label: "Under audit", sub: "Auditing the extracted claims for accuracy…" };
  if (proc.status === "processing" || proc.status === "queued")
    return { label: STAGE_LABELS[proc.stage] || "Processing", sub: proc.message || "revQ is reading your EOB…" };
  return null;
}

// Inline EOB → ERA panel — renders inside the "Scan with Lens" modal.
export default function EobEraFlow({ file, onBack, autoLink, eraViewOpen, onEraViewChange, onChooseFlow, onFinish }) {
  const [stage, setStage] = useState("uploading"); // uploading | processing | result
  const [uploadPct, setUploadPct] = useState(0);
  const [step, setStep] = useState(0);            // flat sub index currently running
  const [elapsed, setElapsed] = useState(0);
  const [fileId, setFileId] = useState(null);
  const [fileEntry, setFileEntry] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [era, setEra] = useState({ available: false, text: "" });
  const [eraFailures, setEraFailures] = useState([]);
  const [eraChecking, setEraChecking] = useState(false);
  const [proc, setProc] = useState(null); // live upstream file-row status while processing
  const showEraView = !!eraViewOpen;
  const setShowEraView = onEraViewChange;
  const [error, setError] = useState(null);
  const runRef = useRef(0);
  const startRef = useRef(null);
  const uploadOnceRef = useRef({ file: null, promise: null });

  const fail = (msg, retry) => setError({ msg, retry });

  const pollUntilDone = async (id, alive) => {
    const t0 = Date.now();
    // Terminal states (per the RevQ API): failed, OR processed + audit complete.
    while (Date.now() - t0 < 180_000) {
      if (!alive()) return null;
      const entry = await getEobFile(id);
      if (entry) {
        setFileEntry(entry);
        setProc(deriveProc(entry));
        if (entry.status === "failed" || entry.status === "error" || entry.job_status === "failed") return entry;
        if (entry.status === "processed") return entry;
      }
      await sleep(2500);
    }
    throw new Error("Processing timed out. The document is taking longer than expected.");
  };

  const loadResult = async (id, alive) => {
    const entry = await getEobFile(id);
    if (!alive()) return;
    setFileEntry(entry);
    const an = await getEobAnalysis(id).catch(() => null);
    if (!alive()) return;
    setAnalysis(an);
    setStep(TOTAL_SUBS);

    // Extraction failed → the ERA gate can't run; the failure UI takes over.
    if (isExtractionFailed(an, entry)) {
      setEra({ available: false, text: "" });
      setStage("result");
      return;
    }

    // Show the "finalizing" loader immediately (batched with the result transition) so the
    // validation-blocked / success state never flashes while linking + validation run.
    setEraChecking(true);
    setStage("result");

    // Experience Mode: auto-link the extracted payee → client id and payer → payer id
    // so the 835 can clear the EHI_CLIENT_ID / EDI_PAYER_ID validation gate.
    const summary = entry?.summary || entry?.extraction_summary || {};
    if (autoLink?.clientId) {
      try {
        if (summary.payee_name) await linkPayee(summary.payee_name, autoLink.clientId);
        if (summary.payee_name && autoLink.payeeNpi) await linkPayeeNpi(summary.payee_name, autoLink.payeeNpi);
        if (summary.payer_name && autoLink.payerId) await linkPayer(summary.payer_name, autoLink.payerId);
      } catch { /* non-fatal — real validation reasons will still surface below */ }
    }
    if (!alive()) return;

    // Gate: the 835 is only produced by run-validation once extraction passes the
    // balancing check. After auto-linking, mapping resolution is eventually consistent —
    // a pass can still report the just-linked blockers, so retry while ONLY those remain.
    const MAPPING_CODES = new Set(["EHI_CLIENT_ID_BLANK", "EDI_PAYER_ID_BLANK", "PAYEE_NPI_BLANK"]);
    const maxAttempts = autoLink?.clientId ? 4 : 1;
    try {
      let v = { generated: 0, failures: [] };
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        if (!alive()) return;
        v = await runEraValidation(id);
        if (v.generated > 0) break;
        const onlyMapping =
          (v.failures || []).length > 0 && v.failures.every((f) => MAPPING_CODES.has(f.code));
        if (!onlyMapping) break; // a genuine blocker (e.g. balancing) — stop retrying
        await sleep(1500);
      }
      if (!alive()) return;

      // Experience Mode: silently accept all non-fundamental review gates
      // (PLB_DERIVED_REVIEW, reconciliation, balance warnings) and re-run so the demo
      // shows a real ERA. Fundamental mapping gates are never bypassed — if they're the
      // only thing left, the validation-blocked panel still shows below.
      if (autoLink?.clientId && v.generated === 0) {
        const hasReviewGate = (v.failures || []).some((f) => !MAPPING_CODES.has(f.code));
        if (hasReviewGate) {
          try {
            await acknowledgeReconciliation(id, "all");
            if (!alive()) return;
            v = await runEraValidation(id);
          } catch { /* non-fatal — surface whatever validation now reports */ }
        }
      }
      if (!alive()) return;
      setEraFailures(v.failures || []);
    } catch { /* gate call itself failed — non-fatal, download attempt below still runs */ }
    const eraResult = await getEra(id).catch(() => ({ available: false, text: "" }));
    if (!alive()) return;
    setEra(eraResult);
    setEraChecking(false);
  };

  const startProcessing = async (id, alive = () => true) => {
    setStage("processing");
    setStep(0);
    setProc(null);
    setError(null);
    try {
      const entry = await pollUntilDone(id, alive);
      if (!entry || !alive()) return;
      await loadResult(id, alive);
    } catch (err) {
      if (!alive()) return;
      fail(describeError(err) || err.message, () => startProcessing(id, alive));
      setStage("processing");
    }
  };

  const start = useCallback(async (f, alive = () => true) => {
    if (!f) return;
    setError(null);
    setUploadPct(0);
    setStage("uploading");
    try {
      // Same file already sent (remount / double-run effect)? Reuse it — never upload twice.
      let uploadP = uploadOnceRef.current.file === f ? uploadOnceRef.current.promise : null;
      if (!uploadP) {
        uploadP = uploadEob(f, { onProgress: setUploadPct });
        uploadOnceRef.current = { file: f, promise: uploadP };
      }
      const done = await uploadP;
      if (!alive()) return;
      setFileId(done.file_id);
      await startProcessing(done.file_id, alive);
    } catch (err) {
      uploadOnceRef.current = { file: null, promise: null }; // let a retry re-upload
      if (!alive()) return;
      fail(describeError(err), () => start(f, alive));
      setStage("uploading");
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  startRef.current = start;

  useEffect(() => {
    const runId = ++runRef.current;
    const alive = () => runRef.current === runId;
    if (file) startRef.current?.(file, alive);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => { runRef.current++; };
  }, [file]);

  // Walk the phase sub-steps while processing; last sub keeps spinning until the real result lands.
  useEffect(() => {
    if (stage !== "processing") return;
    setElapsed(0);
    const t0 = Date.now();
    const tick = setInterval(() => setElapsed((Date.now() - t0) / 1000), 100);
    const adv = setInterval(() => setStep((s) => (s < TOTAL_SUBS - 1 ? s + 1 : s)), 1350);
    return () => { clearInterval(tick); clearInterval(adv); };
  }, [stage]);

  const download835 = () => {
    if (!era.text) return;
    const blob = new Blob([era.text], { type: "application/edi-x12" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(file?.name || "eob").replace(/\.[^.]+$/, "")}.era`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    if (fileId) markEraDownloaded(fileId);
  };

  const summary = fileEntry?.summary || fileEntry?.extraction_summary || {};
  const failed = stage === "result" && isExtractionFailed(analysis, fileEntry);
  const showPipeline = stage === "uploading" || stage === "processing" || (stage === "result" && !failed);
  const processing = stage === "processing";

  // Auto-scroll: follow the pipeline — keep the currently-running step in view as it advances.
  const followRef = useAutoScroll(showPipeline ? step : null, { block: "center" });

  // Sticky footer states — the bottom bar is always visible and reflects the pipeline outcome.
  const stickyReady = !error && stage === "result" && !eraChecking && !failed && era.available;
  const stickyFailed = !!error || (stage === "result" && !eraChecking && (failed || !era.available));
  const live = liveStatus(proc);
  const loadingLabel = eraChecking
    ? "Assembling your 835 ERA…"
    : stage === "uploading"
    ? "Uploading & preparing your document…"
    : (live?.label || "Converting your EOB to an ERA…");
  const loadingSub = stage === "uploading" || eraChecking
    ? "revQ is working through the pipeline — this only takes a few seconds."
    : (live?.sub || "revQ is working through the pipeline — this only takes a few seconds.");
  const retry = () => {
    if (error?.retry) { const r = error.retry; setError(null); return r(); }
    setEra({ available: false, text: "" });
    setEraFailures([]);
    const runId = ++runRef.current;
    const alive = () => runRef.current === runId;
    if (failed) return start(file, alive);
    if (fileId) return startProcessing(fileId, alive);
    return start(file, alive);
  };

  const payerText = summary.payer_name
    ? <><b className="text-[var(--ink)]">{summary.payer_name}</b>{summary.check_number ? <span className="text-[var(--gray2)]"> · check {summary.check_number}</span> : null}{(summary.check_amount ?? summary.total_paid) != null ? <span className="text-[var(--gray2)]"> · {money(summary.check_amount ?? summary.total_paid)}</span> : null}</>
    : <span className="text-[var(--gray2)]">{stage === "result" ? "—" : "Reading…"}</span>;
  const patientName = summary.patient_name || summary.patient;
  const patientText = patientName
    ? <><b className="text-[var(--ink)]">{patientName}</b>{(summary.date_of_service || summary.dos) ? <span className="text-[var(--gray2)]"> · DOS {summary.date_of_service || summary.dos}</span> : null}</>
    : <span className="text-[var(--gray2)]">{stage === "result" ? "—" : "Identifying…"}</span>;

  if (showEraView) {
    return (
      <EraViewModal
        analysis={analysis}
        eraText={era.text}
        filename={file?.name}
        originalFile={file}
        onDownload={download835}
        onBack={() => setShowEraView(false)}
        onChooseFlow={onChooseFlow}
        onFinish={onFinish}
      />
    );
  }

  return (
    <div className="px-6 pt-4 pb-4" data-testid={TID.eobEraPanel}>
      {/* Title row */}
      <div className="flex items-center gap-2.5 mb-3">
        <div className="w-6 h-6 rounded-[7px] bg-[var(--ink)] text-white flex items-center justify-center flex-shrink-0">
          <Layers size={13} />
        </div>
        <div className="text-[13px] font-bold text-[var(--ink)]">EOB → ERA conversion</div>
        <span className="ml-auto text-[9.5px] font-semibold uppercase tracking-[.04em] text-[var(--gray2)] bg-black/[.05] border border-black/[.08] rounded-full px-2 py-0.5" data-testid="eob-era-engine">{ENGINE_LABEL}</span>
      </div>

      {/* Error banner (message only — Retry lives in the sticky footer) */}
      {error ? (
        <div data-testid={TID.eobEraError}
          className="mb-4 rounded-[12px] border border-[rgba(255,159,10,.35)] bg-[rgba(255,159,10,.1)] px-3.5 py-3 flex items-start gap-2.5">
          <AlertTriangle size={15} className="text-[var(--amber,#c0392b)] mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-[11.5px] font-semibold text-[var(--ink)]">Something went wrong</div>
            <div className="text-[11px] text-[var(--gray2)] mt-0.5">{error.msg}</div>
          </div>
        </div>
      ) : null}

      {/* PIPELINE (upload + processing + success) */}
      {showPipeline ? (
        <div data-testid={stage === "result" ? TID.eobEraSummary : TID.eobEraProcessing}>
          {/* Reading panel */}
          <div className="rounded-[13px] border border-black/[.07] bg-black/[.028] px-4 py-3 mb-3">
            <Row label="Reading">
              <b className="text-[var(--ink)]">{file?.name}</b>
              <span className="text-[var(--gray2)]"> · ~11s</span>
            </Row>
            <Row label="Payer">{payerText}</Row>
            <Row label="Patient">{patientText}</Row>
          </div>

          {/* Upload tile — appears above the phase tiles */}
          {(() => {
            const uploading = stage === "uploading";
            const uDone = !uploading;
            return (
              <div className={`rounded-[13px] border mb-2.5 overflow-hidden transition-colors ${
                uDone ? "border-[rgba(52,199,89,.25)] bg-[rgba(52,199,89,.045)]"
                  : "border-black/[.16] bg-white/50 shadow-[0_4px_16px_rgba(0,0,0,.06)]"
              }`} data-testid="eob-era-upload-tile">
                <div className="flex items-center gap-2.5 px-3.5 py-2.5">
                  <div className={`w-[30px] h-[30px] rounded-[9px] flex items-center justify-center flex-shrink-0 border transition-colors ${
                    uDone ? "bg-[var(--green,#34c759)] border-[var(--green,#34c759)] text-white"
                      : "bg-[rgba(255,159,10,.14)] border-[rgba(255,159,10,.3)] text-[var(--amber-deep,#a15c00)]"
                  }`}>
                    <ScanLine size={15} strokeWidth={1.8} />
                  </div>
                  <div className="text-[12.5px] font-bold text-[var(--ink)]">Upload document</div>
                  {uDone ? null : (
                    <span className="ml-auto text-[10px] font-semibold text-[var(--amber-deep,#a15c00)] tabular-nums">{uploadPct}%</span>
                  )}
                </div>
                <div className="px-3.5 pb-2.5">
                  <div className="flex items-start gap-3 py-1.5 pl-4 ml-3.5 border-l-2 border-black/[.07]">
                    <span className={`w-[15px] h-[15px] rounded-full flex-shrink-0 mt-0.5 flex items-center justify-center border-[1.6px] box-border ${
                      uDone ? "bg-[var(--green,#34c759)] border-[var(--green,#34c759)] text-white"
                        : "border-[var(--amber-deep,#a15c00)] border-t-transparent animate-spin"
                    }`}>
                      {uDone ? <Check size={9} /> : null}
                    </span>
                    <div className="min-w-0">
                      <div className="text-[11.5px] leading-[1.4] font-medium text-[var(--ink2)] truncate">
                        {uploading ? `Uploading ${file?.name}…` : `${file?.name} uploaded`}
                      </div>
                      {uploading ? (
                        <div className="mt-1.5 h-[5px] w-[180px] max-w-full bg-black/[.08] rounded-full overflow-hidden">
                          <div className="h-full bg-[var(--amber-deep,#a15c00)] rounded-full transition-all duration-300" style={{ width: `${uploadPct}%` }} />
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Phase cards */}
          {EOB_PHASES.map((p, pi) => {
            const start = phaseStart(pi);
            const phaseDone = start + p.subs.length <= step;
            const phaseRun = processing && p.subs.some((_, si) => start + si === step);
            return (
              <PipelineTile key={p.t} icon={p.icon} title={p.t} done={phaseDone} run={phaseRun}
                innerRef={phaseRun ? followRef : undefined}
                subs={p.subs.map((s, si) => {
                  const run = processing && start + si === step;
                  const isLast = start + si === TOTAL_SUBS - 1;
                  const liveSub = isLast && run ? live : null;
                  return {
                    key: s.t,
                    title: liveSub?.label || s.t,
                    sub: liveSub?.sub || s.s,
                    done: start + si < step,
                    run,
                  };
                })}
              />
            );
          })}

          {/* ERA assembly — 5th pipeline tile (only while finalizing; disappears once ERA is ready) */}
          {stage === "result" && era.available ? null : (() => {
            const eraRun = eraChecking;
            return (
              <div className={`rounded-[13px] border mb-2.5 overflow-hidden transition-colors ${
                eraRun ? "border-black/[.16] bg-white/50 shadow-[0_4px_16px_rgba(0,0,0,.06)]"
                  : "border-black/[.07] bg-white/50"
              }`} data-testid="eob-era-assembly-tile">
                <div className="flex items-center gap-2.5 px-3.5 py-2.5">
                  <div className={`w-[30px] h-[30px] rounded-[9px] flex items-center justify-center flex-shrink-0 border transition-colors ${
                    eraRun ? "bg-[rgba(255,159,10,.14)] border-[rgba(255,159,10,.3)] text-[var(--amber-deep,#a15c00)]"
                      : "bg-black/[.05] border-black/[.07] text-[var(--gray1)]"
                  }`}>
                    <Layers size={15} strokeWidth={1.8} />
                  </div>
                  <div className="text-[12.5px] font-bold text-[var(--ink)]">Assembling ERA (835)</div>
                </div>
                <div className="px-3.5 pb-2.5">
                  <div className="flex items-start gap-3 py-1.5 pl-4 ml-3.5 border-l-2 border-black/[.07]">
                    <span className={`w-[15px] h-[15px] rounded-full flex-shrink-0 mt-0.5 flex items-center justify-center border-[1.6px] box-border ${
                      eraRun ? "border-[var(--amber-deep,#a15c00)] border-t-transparent animate-spin"
                        : "border-[var(--gray4,#c7c7cc)]"
                    }`} />
                    <div className="min-w-0">
                      <div className={`text-[11.5px] leading-[1.4] ${eraRun ? "font-semibold text-[var(--ink)]" : "font-medium text-[var(--ink2)]"}`}>
                        Validating mappings & balancing the remittance
                      </div>
                      {eraRun ? <div className="text-[10px] text-[var(--gray2)] mt-0.5 leading-[1.4]">Assembling the X12 835 and posting against the matched claim…</div> : null}
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      ) : null}

      {/* Details (informational, above the sticky footer) */}
      {failed ? (
        <div className="rounded-[14px] border border-[rgba(255,159,10,.35)] bg-[rgba(255,159,10,.1)] p-4">
          <div className="flex items-center gap-2.5">
            <AlertTriangle size={17} className="text-[var(--amber,#c0392b)]" />
            <div className="text-[13px] font-semibold text-[var(--ink)]">Extraction needs another pass</div>
          </div>
          <div className="text-[11.5px] text-[var(--gray2)] mt-2 leading-snug">{extractionFailureReason(analysis, fileEntry)}</div>
        </div>
      ) : stage === "result" && !eraChecking && !era.available && eraFailures.length ? (
        <div className="rounded-[14px] border border-black/[.08] bg-white/60 p-4" data-testid={TID.eobEraValidationFailures}>
          <div className="flex items-start gap-2.5">
            <AlertTriangle size={16} className="text-[var(--amber,#c0392b)] mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-[12px] font-semibold text-[var(--ink)]">These blocked the 835 from assembling:</div>
              <ul className="mt-2.5 space-y-1.5">
                {eraFailures.map((f, i) => (
                  <li key={i} className="rounded-[10px] border border-[rgba(255,159,10,.28)] bg-[rgba(255,159,10,.07)] px-3 py-2">
                    <div className="text-[9px] font-bold uppercase tracking-[.04em] text-[var(--amber-deep,#a15c00)]">
                      {(f.code || "REVIEW").replace(/_/g, " ")}
                    </div>
                    <div className="text-[11px] text-[var(--ink2)] mt-0.5 leading-snug">{f.reason}</div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      ) : null}

      {/* STICKY FOOTER — always visible: loading / ready / failed */}
      <StatusFooter
        testId="eob-era-sticky"
        state={stickyReady ? "ready" : stickyFailed ? "failed" : "loading"}
        innerTestId={stickyReady ? "eob-era-ready-sticky" : stickyFailed ? "eob-era-failed-sticky" : "eob-era-loading-sticky"}
        eyebrow={stickyReady ? "835 ERA generated" : stickyFailed ? "conversion incomplete" : undefined}
        title={stickyReady ? "Your ERA is ready"
          : stickyFailed ? (error ? "Something went wrong" : failed ? "Extraction needs another pass" : "835 ERA not generated")
          : loadingLabel}
        subtitle={stickyReady ? "Standard X12 835 remittance — view the details or download it."
          : stickyFailed ? (error ? error.msg : failed ? "Lens couldn't fully read this document." : "Validation blocked — resolve the items above and retry.")
          : loadingSub}
        action={stickyReady
          ? { label: "View ERA", icon: <Eye size={14} />, onClick: () => setShowEraView(true), testId: TID.eraViewOpenBtn }
          : stickyFailed
          ? { label: "Retry", icon: <RefreshCw size={14} />, onClick: retry, testId: "eob-era-retry-btn" }
          : null}
      />
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex gap-2.5 text-[11.5px] leading-[1.55] py-0.5">
      <span className="text-[8.5px] font-bold tracking-[.05em] uppercase text-[var(--gray3)] w-[74px] flex-shrink-0 pt-0.5">{label}</span>
      <span className="text-[var(--ink2)] min-w-0">{children}</span>
    </div>
  );
}
